import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Heading, HStack, Image, StackDivider } from '@chakra-ui/react';

import { getMarketplaceLogo } from 'components/marketplaceLogo';

import TabNavigation from './components/TabNavigation';
import ConfigurationCards from './components/ConfigurationCards';
import SourcingRulesTable from './components/SourcingRulesTable';
import StorePoliciesTab from './components/StorePoliciesTab';
import StoreDetails from './components/StoreDetails';
import { useColors } from './components/colors';

import { getStoreApi, updateStoreApi } from 'features/stores/stores.api';
import { getSuppliersListApi } from 'features/suppliers/suppliers.api';

const Store = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { store, getStoreStatus, updateStoreStatus } = useSelector(
    (state) => state.stores,
  );

  const [currentTab, setCurrentTab] = useState(0);
  const [storeData, setStoreData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { borderColor, headingBgColor, textColor } = useColors();

  useEffect(() => {
    if (id) {
      dispatch(getStoreApi(id));
    }
    dispatch(getSuppliersListApi());
  }, [dispatch, id, updateStoreStatus]);

  useEffect(() => {
    if (store) {
      setStoreData(store);
    }
  }, [store]);

  const handleStoreUpdate = (updatedData) => {
    setStoreData(updatedData);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (storeData && id) {
      await dispatch(updateStoreApi({ id, data: storeData })).unwrap();
      setHasChanges(false);
    }
  };

  if (getStoreStatus === 'pending' || !storeData) {
    return (
      <Container maxW="container.xl" py={8} mt="50px">
        Loading...
      </Container>
    );
  }

  const currentLogo = getMarketplaceLogo(storeData.marketplace?.name) || null;
  const isEbay = storeData?.marketplace?.name?.toLowerCase() === 'ebay';

  return (
    <Container maxW="container.xl" py={8} mt="50px">
      <HStack
        spacing={4}
        mb={6}
        align="center"
        divider={
          <StackDivider
            orientation="vertical"
            borderColor={borderColor}
            borderWidth="1px"
          />
        }
        bgColor={headingBgColor}
        p="30px 20px"
        borderRadius="lg"
      >
        <Heading as="h1" size="xl" color={textColor}>
          {storeData.name}
        </Heading>

        {currentLogo && (
          <Image
            src={currentLogo}
            alt={storeData.marketplace?.name}
            maxH="30px"
            objectFit="contain"
          />
        )}
      </HStack>

      <TabNavigation currentTab={currentTab} onTabChange={setCurrentTab} isEbay={isEbay} />

      {currentTab === 0 && (
        <ConfigurationCards
          storeData={storeData}
          onStoreUpdate={handleStoreUpdate}
          hasChanges={hasChanges}
          onSave={handleSave}
          isLoading={updateStoreStatus === 'pending'}
        />
      )}
      {currentTab === 1 && (
        <SourcingRulesTable
          storeData={storeData}
          onStoreUpdate={handleStoreUpdate}
          hasChanges={hasChanges}
          onSave={handleSave}
          isLoading={updateStoreStatus === 'pending'}
        />
      )}
      {isEbay && currentTab === 2 && <StorePoliciesTab storeId={id} />}
      {((isEbay && currentTab === 3) || (!isEbay && currentTab === 2)) && (
        <StoreDetails
          storeData={storeData}
          onStoreUpdate={handleStoreUpdate}
          hasChanges={hasChanges}
          onSave={handleSave}
          isLoading={updateStoreStatus === 'pending'}
        />
      )}
    </Container>
  );
};

export default Store;
